/**
 * Admin > Site settings — edit site text / config and flip feature switches
 * with no deploy. Values are JSON (text, number, true/false, object/array).
 */
import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Save, Trash2, Globe, Lock, RefreshCw, Megaphone } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const SHOW_ON = [
  { v: "all", label: "Everywhere" },
  { v: "public", label: "Public pages only" },
  { v: "app", label: "Logged-in app only" },
];

const AnnouncementBarForm = ({ initial, onSaved }) => {
  const base = { enabled: false, text: "", link_url: "", link_label: "", style: "green", show_on: "all" };
  const [form, setForm] = useState({ ...base, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-settings/announcement_bar`, { value: form, is_public: true });
      toast.success("Announcement bar saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const green = form.style === "green";
  return (
    <section className="space-y-3 p-4 rounded-lg border border-border" data-testid="announcement-bar-form">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-medium">Announcement bar</h2>
      </div>

      {/* Live preview */}
      <div data-testid="announcement-preview" className={`rounded-lg overflow-hidden ${green ? "bg-success text-white" : "bg-secondary text-foreground border border-border"}`}>
        {form.enabled && form.text ? (
          <div className="px-4 py-2.5 text-sm text-center flex items-center justify-center gap-3 flex-wrap">
            <span className="font-medium">{form.text}</span>
            {form.link_url && form.link_label && <span className="underline font-semibold">{form.link_label}</span>}
          </div>
        ) : (
          <div className="px-4 py-2.5 text-xs text-center text-muted-foreground">Bar is hidden (enable it and add text to preview)</div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="ann-enabled" />
        Enabled (show the bar)
      </label>
      <input value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Announcement text" data-testid="ann-text" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      <div className="grid grid-cols-2 gap-2">
        <input value={form.link_label} onChange={(e) => set("link_label", e.target.value)} placeholder="Link label (optional)" data-testid="ann-link-label" className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="Link URL (optional)" data-testid="ann-link-url" className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.style} onChange={(e) => set("style", e.target.value)} data-testid="ann-style" className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
          <option value="green">Green</option>
          <option value="neutral">Neutral</option>
        </select>
        <select value={form.show_on} onChange={(e) => set("show_on", e.target.value)} data-testid="ann-show-on" className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
          {SHOW_ON.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      </div>
      <button onClick={save} disabled={saving} data-testid="ann-save" className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50">
        <Save className="w-4 h-4" /> Save announcement bar
      </button>
    </section>
  );
};

const valueToText = (v) =>
  typeof v === "string" ? v : JSON.stringify(v, null, 2);

// Parse editor text back to JSON; fall back to raw string if not valid JSON.
const textToValue = (t) => {
  const s = (t ?? "").trim();
  if (s === "") return "";
  try {
    return JSON.parse(s);
  } catch {
    return t;
  }
};

const AdminSiteSettingsPage = () => {
  const [settings, setSettings] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [newSwitch, setNewSwitch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/admin/site-settings`);
      setSettings(data.settings || []);
      const d = {};
      (data.settings || []).forEach((s) => {
        d[s.key] = { text: valueToText(s.value), is_public: s.is_public };
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    const d = drafts[key];
    setSaving(key);
    try {
      await api.put(`/admin/site-settings/${encodeURIComponent(key)}`, {
        value: textToValue(d.text),
        is_public: d.is_public,
      });
      toast.success(`Saved ${key}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete setting "${key}"?`)) return;
    try {
      await axios.delete(`${API}/admin/site-settings/${encodeURIComponent(key)}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const createKey = async (key, value, is_public) => {
    if (!key.trim()) return;
    try {
      await api.post(`/admin/site-settings`, { key: key.trim(), value, is_public });
      toast.success(`Created ${key.trim()}`);
      setNewKey(""); setNewSwitch("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const toggleSwitch = async (s) => {
    try {
      await api.put(`/admin/site-settings/${encodeURIComponent(s.key)}`, {
        value: !(s.value === true),
        is_public: s.is_public,
      });
      toast.success(`${s.key} is now ${!(s.value === true) ? "ON" : "OFF"}`);
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const features = settings.filter((s) => s.key.startsWith("features."));
  const annBar = settings.find((s) => s.key === "announcement_bar")?.value;
  const regular = settings.filter((s) =>
    !s.key.startsWith("features.") &&
    !s.key.startsWith("homepage.") &&
    s.key !== "announcement_bar",
  );

  if (loading) return <div className="p-6 text-muted-foreground" data-testid="site-settings-loading">Loading…</div>;

  return (
    <div className="p-6 space-y-8 max-w-4xl" data-testid="admin-site-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Site settings</h1>
          <p className="text-sm text-muted-foreground">Edit site text and config with no deploy. Public values appear on the live site within a minute.</p>
        </div>
        <button onClick={load} data-testid="refresh-settings" className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-border hover:bg-secondary">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <AnnouncementBarForm initial={annBar} onSaved={load} />

      {/* Feature switches */}
      <section className="space-y-3" data-testid="feature-switches-section">
        <h2 className="text-lg font-medium">Feature switches</h2>
        {features.length === 0 && <p className="text-sm text-muted-foreground">No switches yet.</p>}
        <div className="space-y-2">
          {features.map((s) => (
            <div key={s.key} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`feature-row-${s.key}`}>
              <div>
                <div className="font-mono text-sm">{s.key}</div>
                <div className="text-xs text-muted-foreground">last changed by {s.updated_by || "—"} · {fmt(s.updated_at)}</div>
              </div>
              <button
                onClick={() => toggleSwitch(s)}
                data-testid={`feature-toggle-${s.key}`}
                className={`relative w-12 h-6 rounded-full transition-colors ${s.value === true ? "bg-primary" : "bg-muted"}`}
                aria-pressed={s.value === true}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${s.value === true ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <span className="font-mono text-sm text-muted-foreground">features.</span>
          <input
            value={newSwitch}
            onChange={(e) => setNewSwitch(e.target.value)}
            placeholder="switch_name"
            data-testid="new-switch-input"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
          />
          <button
            onClick={() => createKey(`features.${newSwitch.trim()}`, false, true)}
            data-testid="add-switch-btn"
            className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground"
          >
            <Plus className="w-4 h-4" /> Add switch (OFF)
          </button>
        </div>
      </section>

      {/* Regular settings */}
      <section className="space-y-3" data-testid="site-settings-section">
        <h2 className="text-lg font-medium">Settings</h2>
        {regular.map((s) => (
          <div key={s.key} className="p-4 rounded-lg border border-border space-y-2" data-testid={`setting-row-${s.key}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono text-sm">{s.key}</div>
              <div className="flex items-center gap-3 text-xs">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={drafts[s.key]?.is_public || false}
                    onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: { ...d[s.key], is_public: e.target.checked } }))}
                    data-testid={`setting-public-${s.key}`}
                  />
                  {drafts[s.key]?.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />} public
                </label>
                <span className="text-muted-foreground">by {s.updated_by || "—"} · {fmt(s.updated_at)}</span>
              </div>
            </div>
            <textarea
              value={drafts[s.key]?.text ?? ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: { ...d[s.key], text: e.target.value } }))}
              rows={valueToText(s.value).split("\n").length > 2 ? 4 : 1}
              data-testid={`setting-value-${s.key}`}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono"
            />
            <div className="flex items-center gap-2">
              <button onClick={() => save(s.key)} disabled={saving === s.key} data-testid={`setting-save-${s.key}`} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground disabled:opacity-50">
                <Save className="w-4 h-4" /> Save
              </button>
              <button onClick={() => remove(s.key)} data-testid={`setting-delete-${s.key}`} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg hover:bg-destructive/15 text-destructive">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2">
          <input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="new.key.name"
            data-testid="new-key-input"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
          />
          <button
            onClick={() => createKey(newKey, "", false)}
            data-testid="add-key-btn"
            className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground"
          >
            <Plus className="w-4 h-4" /> Add key
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminSiteSettingsPage;
